/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Typography2, breakpoints, typography } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';

type Props = {
  quoteText: string;
  authorName: string;
  authorTitle?: string | null;
  authorOrg?: string | null;
  index?: number;
};

const styles = {
  quoteContainer: css`
    padding: var(--cds-spacing-400);
  `,
  leftOpenQuote: css`
    font-size: 80px;
    font-weight: 600;
    line-height: 80px;
    text-align: left;
    ${breakpoints.down('xs')} {
      letter-spacing: 0;
    }
    ${breakpoints.up('sm')} {
      letter-spacing: 16px;
    }
  `,
  textContainer: () => css`
    width: 80%;
  `,
  authorContainer: () => css`
    padding-top: 16px;
  `,
  blockQuote: () => css`
    border-left: none;
    padding-left: 0;
    margin-bottom: 0;
  `,
  quoteText: css`
    .rc-Markdown.styled {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p,
      li {
        font-family: var(--cds-font-family-source-sans-pro);
        ${typography.h2}
        color: var(--cds-color-neutral-primary);
      }

      a {
        color: var(--cds-color-interactive-primary);

        &:hover,
        &:focus {
          text-decoration: none;
        }

        &:visited {
          color: var(--cds-color-interactive-primary-pressed);
        }
      }
    }
  `,
};

export const Quote = ({ quoteText, authorName, authorTitle, authorOrg, index }: Props) => {
  const { useGetViewTestimonialCardRef } = useDegreeDescriptionEventingContext();
  const { id } = useDegreeDescriptionPageData();
  const degreeId = id.replace(/^base~/, '');
  const viewRef = useGetViewTestimonialCardRef(quoteText, authorName, authorTitle, index);

  return (
    <div css={styles.quoteContainer} ref={viewRef}>
      <Grid container>
        <Grid item>
          <Typography2 component="p" display="inline" css={styles.leftOpenQuote}>
            &ldquo;
          </Typography2>
        </Grid>

        <Grid item css={styles.textContainer}>
          <Grid container item direction="column">
            <Grid item css={styles.quoteText}>
              <blockquote css={styles.blockQuote}>
                <Markdown
                  source={quoteText}
                  trackingName="testimonial_link"
                  buttonTrackingNameV3="degree_info_link"
                  trackingSection="learner_testimonials"
                  trackingProductId={degreeId}
                />
              </blockquote>
            </Grid>
            <Grid item css={styles.authorContainer}>
              <Typography2 component="h3" variant="subtitleMedium">
                {authorName}
              </Typography2>
            </Grid>
            <Grid item>
              <Typography2 component="p" variant="bodySecondary" color="supportText">
                {authorTitle}
              </Typography2>
            </Grid>
            <Grid item>
              <Typography2 component="p" variant="bodySecondary" color="supportText">
                {authorOrg}
              </Typography2>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default Quote;
